import React, { useMemo, useState } from 'react';
import { Box, CircleDot, Ruler } from 'lucide-react';

type View = 'side' | 'front' | 'top';

type Props = {
  axis: 'longitudinal' | 'transverse';
  outerDiameterM: number;
  innerDiameterM: number;
  coilWidthM: number;
  beamSpreadM: number;
  providedChockHeightM: number;
  cogHeightM: number;
  requiredBeddingLengthM?: number;
  requiredChockHeightM?: number;
  chockPasses?: boolean;
  directLashingCount: number;
  directLashingAttachmentHeightM: number;
  directLashingVerticalAngleDeg: number;
  directLashingHorizontalAngleDeg: number;
};

const fmt = (value: number) => `${value.toFixed(2)} m`;

export default function SteelCoilTechnicalDrawing(props: Props) {
  const [view, setView] = useState<View>('side');
  const drawing = useMemo(() => {
    const diameter = Math.max(props.outerDiameterM, 0.1);
    const inner = Math.max(0, Math.min(props.innerDiameterM, diameter * 0.9));
    const width = Math.max(props.coilWidthM, 0.1);
    const bedding = Math.max(props.requiredBeddingLengthM || width, width);
    const chockRatio = Math.min(Math.max(props.providedChockHeightM / diameter, 0.03), 0.35);
    return { diameter, inner, width, bedding, chockRatio };
  }, [props]);

  const chockColor = props.chockPasses === false ? '#dc2626' : '#d97706';
  const dimensionColor = '#64748b';

  const Side = () => {
    const radius = 92;
    const innerRadius = radius * (drawing.inner / drawing.diameter);
    const chockHeight = Math.max(12, 2 * radius * drawing.chockRatio);
    const attachY = Math.max(72, Math.min(246, 252 - (props.directLashingAttachmentHeightM / drawing.diameter) * 184));
    const verticalAngle = props.directLashingVerticalAngleDeg * Math.PI / 180;
    const run = Math.min(125, Math.max(25, (252 - attachY) / Math.max(Math.tan(verticalAngle), 0.1)));
    const directCount = Math.min(Math.max(Math.round(props.directLashingCount), 0), 6);
    return <svg viewBox="0 0 640 330" className="h-full w-full" role="img" aria-label="Steel coil yan görünüş">
      <defs><marker id="arrow-side" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill={dimensionColor} /></marker></defs>
      <line x1="70" y1="270" x2="570" y2="270" stroke="#94a3b8" strokeWidth="2" />
      <rect x="130" y="255" width="380" height="15" fill="#8b5e3c" rx="2" />
      <circle cx="320" cy="160" r={radius} fill="#64748b" stroke="#334155" strokeWidth="4" />
      <circle cx="320" cy="160" r={innerRadius} fill="#f8fafc" stroke="#334155" strokeWidth="3" />
      <path d={`M${320-radius-8},252 L${320-radius+40},252 L${320-radius+40},${252-chockHeight} Z`} fill={chockColor} stroke="#92400e" strokeWidth="2" />
      <path d={`M${320+radius+8},252 L${320+radius-40},252 L${320+radius-40},${252-chockHeight} Z`} fill={chockColor} stroke="#92400e" strokeWidth="2" />
      {Array.from({ length: directCount }, (_, index) => {
        const right = index % 2 === 1;
        const offset = Math.floor(index / 2) * 5;
        const startX = 320 + (right ? 1 : -1) * (radius * 0.72 - offset);
        const endX = startX + (right ? 1 : -1) * run;
        return <line key={`direct-side-${index}`} x1={startX} y1={attachY + offset} x2={endX} y2="252" stroke="#f59e0b" strokeWidth="3" />;
      })}
      <circle cx="320" cy={252 - (props.cogHeightM / drawing.diameter) * 184} r="6" fill="#ef4444" />
      <line x1="310" y1={252 - (props.cogHeightM / drawing.diameter) * 184} x2="330" y2={252 - (props.cogHeightM / drawing.diameter) * 184} stroke="#ef4444" strokeWidth="2" />
      <line x1="320" y1={242 - (props.cogHeightM / drawing.diameter) * 184} x2="320" y2={262 - (props.cogHeightM / drawing.diameter) * 184} stroke="#ef4444" strokeWidth="2" />
      <text x="337" y={247 - (props.cogHeightM / drawing.diameter) * 184} fontSize="12" fill="#b91c1c">CoG</text>
      <line x1="215" y1="45" x2="425" y2="45" stroke={dimensionColor} markerStart="url(#arrow-side)" markerEnd="url(#arrow-side)" />
      <text x="320" y="35" textAnchor="middle" fontSize="12" fill={dimensionColor}>Ø {fmt(drawing.diameter)}</text>
      <line x1="130" y1="300" x2="510" y2="300" stroke={dimensionColor} markerStart="url(#arrow-side)" markerEnd="url(#arrow-side)" />
      <text x="320" y="320" textAnchor="middle" fontSize="12" fill={dimensionColor}>Bedding {fmt(drawing.bedding)}</text>
    </svg>;
  };

  const Front = () => {
    const coilW = Math.min(260, 100 + drawing.width * 110);
    return <svg viewBox="0 0 640 330" className="h-full w-full" role="img" aria-label="Steel coil ön görünüş">
      <defs><marker id="arrow-front" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill={dimensionColor} /></marker></defs>
      <line x1="70" y1="270" x2="570" y2="270" stroke="#94a3b8" strokeWidth="2" />
      <rect x={320-coilW/2} y="78" width={coilW} height="174" rx="5" fill="#64748b" stroke="#334155" strokeWidth="4" />
      <path d={`M${320-coilW/2-12},252 L${320-coilW/2+38},252 L${320-coilW/2+38},220 Z`} fill={chockColor} />
      <path d={`M${320+coilW/2+12},252 L${320+coilW/2-38},252 L${320+coilW/2-38},220 Z`} fill={chockColor} />
      <line x1={320-coilW/2} y1="45" x2={320+coilW/2} y2="45" stroke={dimensionColor} markerStart="url(#arrow-front)" markerEnd="url(#arrow-front)" />
      <text x="320" y="35" textAnchor="middle" fontSize="12" fill={dimensionColor}>Genişlik {fmt(drawing.width)}</text>
      <line x1="155" y1="286" x2="485" y2="286" stroke={dimensionColor} markerStart="url(#arrow-front)" markerEnd="url(#arrow-front)" />
      <text x="320" y="310" textAnchor="middle" fontSize="12" fill={dimensionColor}>Kiriş açıklığı {fmt(props.beamSpreadM)}</text>
    </svg>;
  };

  const Top = () => {
    const longitudinal = props.axis === 'longitudinal';
    const cargoW = longitudinal ? 150 : 250;
    const cargoH = longitudinal ? 230 : 130;
    const directCount = Math.min(Math.max(Math.round(props.directLashingCount), 0), 6);
    const horizontalAngle = props.directLashingHorizontalAngleDeg * Math.PI / 180;
    return <svg viewBox="0 0 640 330" className="h-full w-full" role="img" aria-label="Steel coil üst görünüş">
      <defs><marker id="force-arrow" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto"><path d="M0,0 L9,4.5 L0,9 z" fill="#dc2626" /></marker></defs>
      <rect x="55" y="30" width="530" height="270" rx="4" fill="#f8fafc" stroke="#94a3b8" strokeWidth="2" />
      <rect x="105" y="120" width="430" height="18" fill="#8b5e3c" /><rect x="105" y="192" width="430" height="18" fill="#8b5e3c" />
      <rect x={320-cargoW/2} y={165-cargoH/2} width={cargoW} height={cargoH} rx="6" fill="#64748b" stroke="#334155" strokeWidth="4" />
      <ellipse cx="320" cy="165" rx={longitudinal ? 42 : 92} ry={longitudinal ? 92 : 42} fill="none" stroke="#cbd5e1" strokeWidth="3" />
      {Array.from({ length: directCount }, (_, index) => {
        const sign = index % 2 === 0 ? -1 : 1;
        const along = (Math.floor(index / 2) - 1) * 28;
        const dx = Math.cos(horizontalAngle) * 85 * sign;
        const dy = Math.sin(horizontalAngle) * 85;
        const startX = longitudinal ? 320 + along : 320 + sign * cargoW * 0.38;
        const startY = longitudinal ? 165 + sign * cargoH * 0.38 : 165 + along;
        return <line key={`direct-top-${index}`} x1={startX} y1={startY} x2={startX + dx} y2={startY + dy} stroke="#f59e0b" strokeWidth="3" />;
      })}
      <line x1="320" y1="165" x2="320" y2="70" stroke="#dc2626" strokeWidth="3" markerEnd="url(#force-arrow)" />
      <line x1="320" y1="165" x2="475" y2="165" stroke="#dc2626" strokeWidth="3" markerEnd="url(#force-arrow)" />
      <text x="330" y="82" fontSize="11" fill="#b91c1c">İleri</text><text x="440" y="154" fontSize="11" fill="#b91c1c">Sağ</text>
      <text x="70" y="52" fontSize="12" fill={dimensionColor}>Eksen: {longitudinal ? 'Boyuna' : 'Enine'}</text>
    </svg>;
  };

  return <section className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-[#182235]">
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
      <div className="flex items-center gap-2"><Ruler className="h-4 w-4 text-orange-500" /><h2 className="text-sm font-bold tracking-normal !text-slate-900 dark:!text-white">2D teknik görünüm</h2></div>
      <div className="flex rounded-md border border-slate-300 p-0.5 dark:border-slate-600">
        {([['side', CircleDot, 'Yan'], ['front', Box, 'Ön'], ['top', Box, 'Üst']] as const).map(([value, Icon, label]) => <button key={value} type="button" onClick={() => setView(value)} className={`flex h-8 items-center gap-1 rounded px-3 text-xs font-semibold ${view === value ? 'bg-orange-600 text-white' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'}`}><Icon className="h-3.5 w-3.5" />{label}</button>)}
      </div>
    </div>
    <div className="aspect-[16/8] min-h-[260px] bg-slate-50 p-3 dark:bg-slate-900/40">{view === 'side' ? <Side /> : view === 'front' ? <Front /> : <Top />}</div>
    <div className="grid grid-cols-2 border-t border-slate-200 text-[11px] dark:border-slate-700 sm:grid-cols-4">
      <span className="px-3 py-2">Dış çap <b>{fmt(drawing.diameter)}</b></span><span className="px-3 py-2">İç çap <b>{fmt(drawing.inner)}</b></span><span className="px-3 py-2">Takoz <b>{fmt(props.providedChockHeightM)}</b></span><span className="px-3 py-2">Bedding <b>{fmt(drawing.bedding)}</b></span>
    </div>
  </section>;
}
